import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import WidgetCard from './WidgetCard';
import MainCardTitle from './MainCardTitle';
import CenteredSpinner from '../../../components/CenteredSpinner';
import DashboardServices from '../../../services/DashboardServices';
import localStorageService from '../../../services/localStorageService';

const COLORS = [
    '#5e35b1', '#7cb342', '#ef5350', '#42a5f5', '#ffa726',
    '#ec407a', '#26a69a', '#ab47bc', '#66bb6a', '#d4a017',
    '#ff7043', '#8d6e63', '#78909c', '#9ccc65', '#7e57c2',
];

const LegendItem = ({ color, label, value, percentage }) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Box
            sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: color,
                flexShrink: 0
            }}
        />
        <Typography variant="body2" sx={{ flex: 1, color: 'text.primary', fontSize: 12 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: 12, minWidth: 50, textAlign: 'right' }}>
            {value} ({percentage}%)
        </Typography>
    </Stack>
);

const CasesByDemographicChart = ({ title = 'Cases by Demographic' }) => {
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState([]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                const storedUser = localStorageService?.getItem?.('user') ?? null;
                const roles = (storedUser?.roles) || (storedUser?.role) || storedUser?.rolesArray || [];
                const normalizedRoles = Array.isArray(roles) ? roles.map(r => String(r).toLowerCase()) : [];
                
                const targetRoles = ['organization admin', 'case officer', 'case manager', 'organization head','director cid', 'director ncpa', 'deputy director','oic','department head','team leader first respondent','call center officer mowca', 'counselling officer', 'legal officer'];
                const hasTargetRole = normalizedRoles.some(r => targetRoles.includes(r));

                const params = {};
                
                if (hasTargetRole) {    
                    const orgId = storedUser?.employee?.organization_id ?? storedUser?.organization_id ?? storedUser?.org?.id ?? null;
                    if (orgId) params.organization_id = orgId;
                }

                const res = await DashboardServices.getDashboardCasesByDemographic(params);
                const payload = res?.data ?? res;
                const dataArray = payload?.view?.data ?? payload?.data ?? [];
                
                // Sort by case_count descending and transform
                const sorted = Array.isArray(dataArray)
                    ? [...dataArray].sort((a, b) => (Number(b.case_count) || 0) - (Number(a.case_count) || 0))
                    : [];

                const formatted = sorted.map((item, index) => ({
                    name: item.demographic ?? `Demographic ${index + 1}`,
                    value: Number(item.case_count) || 0,
                    percentage: Number(item.percentage) || 0,
                    color: COLORS[index % COLORS.length]
                }));

                if (mounted) setChartData(formatted);
            } catch (err) {
                console.error('Failed to load cases by demographic', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, []);

    //if (loading) return <WidgetCard sx={{ minHeight: 400 }}><CenteredSpinner /></WidgetCard>;

    return (
        <WidgetCard sx={{ minHeight: 400 }}>
            <MainCardTitle>{title}</MainCardTitle>

            {chartData.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    No data available
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                    {/* Chart */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart 
                                data={chartData}
                                margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                <XAxis 
                                    dataKey="name"
                                    tick={false}
                                    axisLine={{ stroke: '#e0e0e0' }}
                                />
                                <YAxis 
                                    tick={{ fill: '#666', fontSize: 12 }}
                                    axisLine={{ stroke: '#e0e0e0' }}
                                />
                                <Tooltip 
                                    contentStyle={{ 
                                        backgroundColor: '#fff', 
                                        border: '1px solid #ccc',
                                        borderRadius: 4
                                    }}
                                    cursor={{ fill: 'rgba(0,0,0,0.05)' }}
                                    formatter={(value, name, props) => [
                                        `${value} (${props.payload.percentage}%)`,
                                        'Cases'
                                    ]}
                                />
                                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>

                    {/* Legend */}
                    <Box sx={{ width: 250, flexShrink: 0, maxHeight: 400, overflowY: 'auto', pr: 1 }}>
                        {chartData.map((item, index) => (
                            <LegendItem
                                key={index}
                                color={item.color}
                                label={item.name}
                                value={item.value}
                                percentage={item.percentage}
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </WidgetCard>
    );
};

export default CasesByDemographicChart;
